import type { AssignableCourier, AssignmentPreview } from "../../api/types";
import { eur } from "../bouquet/builderShared";
import { localDayKey } from "../../utils/dayKey";
import {
  REMOTE_ORDER_SOURCES,
  STAFF_ORDER_SOURCE_OPTIONS,
  orderSourceLabel,
  type CheckoutDraft,
  type DeliveryLegPlan,
} from "./orderBuild";

interface Props {
  draft: CheckoutDraft;
  onChange(next: CheckoutDraft): void;
  couriers: AssignableCourier[];
  couriersLoading: boolean;
  payoutPreview: AssignmentPreview | null;
  payoutPreviewLoading: boolean;
  payoutPreviewError: string | null;
  itemsSummary: string;
  agreedTotal: number | null;
  /**
   * Edit mode: how the order arrived is a fact recorded once — the update
   * path does not send it, and an adapter source (Shopify, Glovo) must
   * never be overwritten — so it is shown as text, not chips (2026-09-09).
   */
  lockedSourceLabel?: string | null;
  submitting: boolean;
  error: string | null;
  onBack(): void;
  onSubmit(): void;
  submitLabel?: string;
  /**
   * G-166 (owner 2026-10-20): "Customer already paid" only when creating —
   * money after creation goes through "Record payment".
   */
  showPaid?: boolean;
  /**
   * G-177 (KI-42, KI-43): what this save may do to the delivery leg — the
   * page works it out once (`deliveryLegPlan`) for the form and the save.
   */
  deliveryPlan: DeliveryLegPlan;
  /** The courier holding the job, shown when the plan locks it. */
  assignedCourierName?: string | null;
  /** The order's job that can no longer be edited: its code and status. */
  closedJobLabel?: string | null;
}

/**
 * Step 2 of the phone "Build & send order" flow: customer + fulfillment +
 * schedule, and (for deliveries) the courier leg. Pure form — validation and
 * submission live in OrderBuildPage.
 */
export function PhoneOrderCheckout({
  draft,
  onChange,
  couriers,
  couriersLoading,
  payoutPreview,
  payoutPreviewLoading,
  payoutPreviewError,
  itemsSummary,
  agreedTotal,
  lockedSourceLabel = null,
  submitting,
  error,
  onBack,
  onSubmit,
  submitLabel = "Send to shop",
  showPaid = true,
  deliveryPlan,
  assignedCourierName = null,
  closedJobLabel = null,
}: Props) {
  const patch = (partial: Partial<CheckoutDraft>) => onChange({ ...draft, ...partial });
  const isPickup = draft.fulfillment === "PICKUP";
  const showDelivery = deliveryPlan.deliveryDetails;
  const { courierLocked } = deliveryPlan;
  // The remote-pickup server rule (G-10, widened by G-56): a customer who
  // ordered by phone / WhatsApp / Instagram and collects in the shop must be
  // reachable; "Other" keeps contact optional.
  const contactRequired = isPickup && REMOTE_ORDER_SOURCES.has(draft.source);
  // G-15: delivery is priced on the courier job, never folded into the
  // order's goods total — but the person on the phone quotes the customer
  // the SUM, so it is shown here, on the step where the delivery price is
  // typed (pre-release fix 2026-09-09: after G-23 reordered the steps this
  // figure was displayed nowhere before "Send to shop").
  const deliveryPrice =
    showDelivery && draft.deliveryPriceEur.trim()
      ? Number(draft.deliveryPriceEur)
      : null;
  const deliveryKnown = deliveryPrice != null && Number.isFinite(deliveryPrice);

  return (
    <div className="pob-checkout">
      <div className="card pob-checkout-summary pob-checkout-summary--breakdown">
        <span>{itemsSummary}</span>
        <dl className="pob-price-breakdown">
          <div className="pob-price-breakdown-sum">
            <dt>Order total (goods)</dt>
            <dd>{agreedTotal != null ? eur(agreedTotal) : "No total agreed yet"}</dd>
          </div>
          {showDelivery ? (
            <>
              <div>
                <dt>Delivery (on the courier job)</dt>
                <dd>{deliveryKnown ? eur(deliveryPrice) : "not set yet"}</dd>
              </div>
              <div className="pob-price-breakdown-sum">
                <dt>Customer pays</dt>
                <dd>
                  {deliveryKnown
                    ? eur((agreedTotal ?? 0) + deliveryPrice)
                    : "—"}
                </dd>
              </div>
            </>
          ) : null}
        </dl>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="modal-form">
          <div className="field">
            <label>How did this order arrive?</label>
            {lockedSourceLabel ? (
              <p className="muted" style={{ margin: 0 }}>
                {lockedSourceLabel} — recorded when the order was taken; not
                edited here.
              </p>
            ) : (
              <div className="pob-chip-row">
                {STAFF_ORDER_SOURCE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={draft.source === option.value}
                    className={draft.source === option.value ? "button" : "button tinted"}
                    onClick={() => patch({ source: option.value })}
                  >
                    {orderSourceLabel(option.value)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="field">
            <label>Fulfillment</label>
            {deliveryPlan.lockedFulfillmentLabel ? (
              <p className="muted" style={{ margin: 0 }}>
                {deliveryPlan.lockedFulfillmentLabel}
              </p>
            ) : (
              <div className="pob-chip-row">
                {/*
                  KI-7 (OD-279): money on an order is only a typed payment, so
                  the choice of fulfilment never ticks "Customer already paid"
                  (a delivery used to, recording a card payment nobody saw).
                */}
                <button
                  type="button"
                  aria-pressed={isPickup}
                  className={isPickup ? "button" : "button tinted"}
                  onClick={() => patch({ fulfillment: "PICKUP" })}
                >
                  Store pickup
                </button>
                <button
                  type="button"
                  aria-pressed={!isPickup}
                  className={!isPickup ? "button" : "button tinted"}
                  onClick={() => patch({ fulfillment: "COURIER" })}
                >
                  Delivery
                </button>
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="pob-customer-name">Customer name *</label>
            <input
              id="pob-customer-name"
              value={draft.customerName}
              onChange={(e) => patch({ customerName: e.target.value })}
              disabled={submitting}
              autoComplete="off"
            />
          </div>
          <div className="field">
            <label htmlFor="pob-customer-phone">
              Phone {contactRequired ? "*" : "(optional)"}
            </label>
            <input
              id="pob-customer-phone"
              type="tel"
              value={draft.customerPhone}
              onChange={(e) => patch({ customerPhone: e.target.value })}
              disabled={submitting}
              autoComplete="off"
            />
          </div>
          <div className="field">
            <label htmlFor="pob-customer-email">
              E-mail {contactRequired ? "* (used to verify the pickup)" : "(optional)"}
            </label>
            <input
              id="pob-customer-email"
              type="email"
              value={draft.customerEmail}
              onChange={(e) => patch({ customerEmail: e.target.value })}
              disabled={submitting}
              autoComplete="off"
            />
          </div>
          <div className="field">
            <label htmlFor="pob-order-type">Order type</label>
            <select
              id="pob-order-type"
              value={draft.orderType}
              onChange={(e) =>
                patch({ orderType: e.target.value as CheckoutDraft["orderType"] })
              }
              disabled={submitting}
            >
              <option value="personal">Personal</option>
              <option value="business">Business</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="pob-date">Day *</label>
            <input
              id="pob-date"
              type="date"
              value={draft.date}
              min={localDayKey(new Date())}
              onChange={(e) => patch({ date: e.target.value })}
              disabled={submitting}
            />
          </div>
          <div className="field">
            <label>Time</label>
            <div className="pob-chip-row">
              {(
                [
                  ["ASAP", "ASAP"],
                  ["EXACT", "Exact time"],
                  ["WINDOW", "Time window"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={draft.timeType === value}
                  className={draft.timeType === value ? "button" : "button tinted"}
                  onClick={() => patch({ timeType: value })}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {draft.timeType !== "ASAP" ? (
            <div className="field">
              <label htmlFor="pob-time-from">
                {draft.timeType === "WINDOW" ? "From *" : "At *"}
              </label>
              <input
                id="pob-time-from"
                type="time"
                value={draft.timeFrom}
                onChange={(e) => patch({ timeFrom: e.target.value })}
                disabled={submitting}
              />
            </div>
          ) : null}
          {draft.timeType === "WINDOW" ? (
            <div className="field">
              <label htmlFor="pob-time-until">Until *</label>
              <input
                id="pob-time-until"
                type="time"
                value={draft.timeUntil}
                onChange={(e) => patch({ timeUntil: e.target.value })}
                disabled={submitting}
              />
            </div>
          ) : null}

          {deliveryPlan.jobClosed ? (
            <div className="field">
              <label>Delivery job</label>
              <p className="muted" style={{ margin: 0 }}>
                {closedJobLabel ?? "—"}
              </p>
            </div>
          ) : null}
          {showDelivery ? (
            <>
              <div className="field">
                <label htmlFor="pob-delivery-address">Delivery address *</label>
                <textarea
                  id="pob-delivery-address"
                  rows={2}
                  value={draft.deliveryAddress}
                  onChange={(e) => patch({ deliveryAddress: e.target.value })}
                  disabled={submitting}
                />
              </div>
              <div className="field">
                <label htmlFor="pob-recipient-name">
                  Recipient name (defaults to customer)
                </label>
                <input
                  id="pob-recipient-name"
                  value={draft.recipientName}
                  onChange={(e) => patch({ recipientName: e.target.value })}
                  disabled={submitting}
                  placeholder={draft.customerName || undefined}
                />
              </div>
              <div className="field">
                <label htmlFor="pob-recipient-phone">Recipient phone</label>
                <input
                  id="pob-recipient-phone"
                  type="tel"
                  value={draft.recipientPhone}
                  onChange={(e) => patch({ recipientPhone: e.target.value })}
                  disabled={submitting}
                />
              </div>
              <div className="field">
                <label htmlFor="pob-recipient-notes">Courier notes</label>
                <textarea
                  id="pob-recipient-notes"
                  rows={2}
                  value={draft.recipientNotes}
                  onChange={(e) => patch({ recipientNotes: e.target.value })}
                  disabled={submitting}
                  placeholder="Door code, floor, leave with neighbour…"
                />
              </div>
              <div className="field">
                <label htmlFor="pob-delivery-price">
                  Delivery price € *{" "}
                  {courierLocked ? <span className="badge muted">courier assigned</span> : null}
                </label>
                <input
                  id="pob-delivery-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.deliveryPriceEur}
                  onChange={(e) => patch({ deliveryPriceEur: e.target.value })}
                  disabled={submitting || courierLocked}
                />
              </div>
              <div className="field">
                <label htmlFor="pob-package-size">Package size</label>
                <select
                  id="pob-package-size"
                  value={draft.packageSize}
                  onChange={(e) =>
                    patch({ packageSize: e.target.value as CheckoutDraft["packageSize"] })
                  }
                  disabled={submitting}
                >
                  <option value="">Not set</option>
                  <option value="SMALL">Small</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LARGE">Large</option>
                </select>
              </div>
              {courierLocked ? (
                <div className="field">
                  <label>Courier</label>
                  <p className="muted" style={{ margin: 0 }}>
                    {assignedCourierName ?? "—"}
                  </p>
                </div>
              ) : null}
              {deliveryPlan.mayAssignCourier ? (
                <>
                <div className="field">
                  <label htmlFor="pob-courier">Assign courier now (optional)</label>
                  <select
                    id="pob-courier"
                    value={draft.courierUserId}
                    onChange={(e) =>
                      patch({
                        courierUserId: e.target.value,
                        overridePayoutEur: "",
                      })
                    }
                    disabled={submitting || couriersLoading}
                  >
                    <option value="">
                      {couriersLoading ? "Loading couriers…" : "Assign later from the Courier board"}
                    </option>
                    {couriers.map((c) => (
                      <option key={c.id} value={c.id} disabled={!c.isAvailable}>
                        {c.fullName}
                        {c.vehicleType ? ` — ${c.vehicleType.toLowerCase()}` : ""}
                        {c.isAvailable ? "" : " (unavailable)"}
                      </option>
                    ))}
                  </select>
                </div>
                {draft.courierUserId ? (
                  <>
                    <div className="field">
                      <label htmlFor="pob-distance">
                        Distance km {payoutPreview?.requiresDistance ? "*" : "(optional)"}
                      </label>
                      <input
                        id="pob-distance"
                        type="number"
                        min={0}
                        step="0.1"
                        value={draft.distanceKm}
                        onChange={(e) => patch({ distanceKm: e.target.value })}
                        disabled={submitting}
                      />
                      {payoutPreview?.requiresDistance ? (
                        <small className="hint">
                          This courier is paid per kilometre, so distance is required.
                        </small>
                      ) : null}
                    </div>

                    {payoutPreviewLoading ? (
                      <p className="pob-payout-note">Calculating this courier&apos;s payout…</p>
                    ) : payoutPreviewError ? (
                      <div className="alert error">{payoutPreviewError}</div>
                    ) : payoutPreview?.payoutEur ? (
                      <div className="pob-payout-preview" aria-live="polite">
                        <span>
                          Courier payout
                          <strong>{eur(Number(payoutPreview.payoutEur))}</strong>
                        </span>
                        <span>
                          Company margin
                          <strong>{eur(Number(payoutPreview.marginEur ?? 0))}</strong>
                        </span>
                      </div>
                    ) : payoutPreview && !payoutPreview.hasRule ? (
                      <div className="field">
                        <label htmlFor="pob-payout-override">Courier payout € *</label>
                        <input
                          id="pob-payout-override"
                          type="number"
                          min={0}
                          step="0.01"
                          value={draft.overridePayoutEur}
                          onChange={(e) => patch({ overridePayoutEur: e.target.value })}
                          disabled={submitting}
                        />
                        <small className="hint">
                          No active payout rule applies to this courier. This amount will be
                          recorded as a manual override.
                        </small>
                      </div>
                    ) : null}
                    <p className="pob-payout-note">
                      The payout uses this courier&apos;s own rule when configured; otherwise the
                      active default rule is used. It is recalculated when assigned.
                    </p>
                  </>
                ) : null}
                </>
              ) : null}
            </>
          ) : null}

          <div className="field">
            <label htmlFor="pob-customer-notes">Order notes (optional)</label>
            <textarea
              id="pob-customer-notes"
              rows={2}
              value={draft.customerNotes}
              onChange={(e) => patch({ customerNotes: e.target.value })}
              disabled={submitting}
            />
          </div>
          {showPaid ? (
            <div className="field">
              <label className="pob-checkbox">
                <input
                  type="checkbox"
                  checked={draft.prepaid}
                  onChange={(e) => patch({ prepaid: e.target.checked })}
                  disabled={submitting}
                />
                <span>Customer already paid</span>
              </label>
              {draft.prepaid ? (
                <div className="pob-deposit-row">
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    step="0.01"
                    placeholder={agreedTotal != null ? "Amount (blank = full)" : "Amount paid"}
                    value={draft.prepaidAmountEur}
                    onChange={(e) => patch({ prepaidAmountEur: e.target.value })}
                    disabled={submitting}
                  />
                  {(["CARD", "CASH", "BANK_TRANSFER"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      aria-pressed={draft.prepaidMethod === m}
                      className={draft.prepaidMethod === m ? "button" : "button tinted"}
                      onClick={() => patch({ prepaidMethod: m })}
                      disabled={submitting}
                    >
                      {m === "CARD" ? "Card" : m === "CASH" ? "Cash" : "Transfer"}
                    </button>
                  ))}
                  <small className="hint">
                    Recorded as a payment on the order — the counter will show
                    the exact balance still due at handover.
                  </small>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="button-row pob-checkout-actions">
          <button type="button" className="button plain" onClick={onBack} disabled={submitting}>
            Back to items
          </button>
          <button className="button" type="submit" disabled={submitting}>
            {submitting ? "Saving…" : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
